/**
 * Deposits & Funding Articles
 */

import type { HelpArticle } from "../types";

export const DEPOSITS_ARTICLES: HelpArticle[] = [
  {
    id: "dep-001",
    slug: "how-deposits-work",
    title: "How Deposits Work",
    description: "Understanding the crypto deposit and conversion process",
    category: "deposits",
    popular: true,
    order: 1,
    keywords: ["deposit", "funding", "crypto", "convert", "process"],
    content: `
# How Deposits Work

Supabank makes converting crypto to fiat simple and automatic.

## Overview

1. **Create Address** - Generate a unique liquidation address
2. **Send Crypto** - Transfer supported cryptocurrency to the address
3. **Automatic Detection** - We detect your deposit on the blockchain
4. **Conversion** - Crypto is converted to fiat at market rate
5. **Payout** - Fiat is sent to your connected bank account

## The Liquidation Process

### Step 1: Address Creation
- Each address is unique to you
- Multiple addresses for different networks
- Addresses never expire

### Step 2: Sending Crypto
- Use any wallet to send
- Ensure correct network selection
- Include sufficient gas/fees

### Step 3: Confirmation
- We wait for network confirmations
- Typically 1-12 confirmations depending on network
- You'll see "Pending" status

### Step 4: Conversion
- Automatic conversion at current rate
- No action required from you
- Competitive exchange rates

### Step 5: Settlement
- Fiat added to your balance
- Automatic payout to bank (if configured)
- Or manual withdrawal

## Timeline

| Network | Confirmations | Typical Time |
|---------|---------------|--------------|
| Ethereum | 12 | ~3 minutes |
| Polygon | 128 | ~5 minutes |
| Base | 12 | ~30 seconds |
| Solana | 32 | ~15 seconds |

## Important Notes

⚠️ **Always verify the network** before sending
⚠️ **Minimum deposit**: Varies by asset and network (use calculator)
⚠️ **Maximum deposit**: Based on your verification tier
    `.trim(),
  },
  {
    id: "dep-002",
    slug: "supported-cryptocurrencies",
    title: "Supported Cryptocurrencies",
    description: "List of cryptocurrencies you can deposit",
    category: "deposits",
    order: 2,
    keywords: ["crypto", "coins", "tokens", "supported", "currencies"],
    content: `
# Supported Cryptocurrencies

Supabank accepts various cryptocurrencies across multiple networks.

## Supported Assets

Supabank supports **8 assets** for liquidation addresses:

### Stablecoins
| Asset | Networks |
|-------|----------|
| USDC | Ethereum, Polygon, Base, Solana, Arbitrum, Stellar |
| USDT | Ethereum, Tron |
| PYUSD | Ethereum, Solana |
| USDB | Base, Solana |
| EURC | Base, Solana |
| DAI | Ethereum |
| USDP | Solana |

### Cryptocurrency
| Asset | Networks |
|-------|----------|
| ETH | Ethereum, Base, Arbitrum, Optimism |

**Important:** Only the 8 assets listed above are supported for liquidation addresses. Bitcoin (BTC), SOL, MATIC, and other cryptocurrencies are **NOT supported** as deposit assets (though some are supported as networks).

## Network Support

### EVM Networks
- Ethereum (ETH)
- Polygon (MATIC)
- Base
- Arbitrum One
- Optimism
- Avalanche C-Chain

### Non-EVM Networks
- Solana
- Tron
- Stellar (coming soon)

## Asset Minimums

Minimum deposit amounts **vary by asset and network**. There is no universal fixed minimum.

### Why Minimums Vary
- **Network fees:** High-fee networks (Ethereum) require higher minimums
- **Asset type:** Stablecoins vs ETH have different requirements
- **Market conditions:** Network congestion affects minimums

### Typical Minimums
| Network Type | Typical Minimum |
|--------------|-----------------|
| Low-fee (Base, Polygon, Solana) | ~$10 |
| High-fee (Ethereum mainnet) | ~$20+ |

### Examples
| Asset | Network | Typical Minimum |
|-------|---------|-----------------|
| USDC | Base | ~$10 |
| USDC | Ethereum | ~$20+ |
| USDT | Ethereum | ~$20+ |
| ETH | Ethereum | ~0.003 ETH |
| USDC | Solana | ~$10 |

**Always use the calculator** in your dashboard to determine the exact minimum for your specific asset and network combination before sending.

## Unsupported Assets

**Do not send:**
- NFTs
- Unsupported tokens
- Assets on wrong network

Sending unsupported assets may result in permanent loss.

## Adding New Assets

We regularly add new assets based on:
- User demand
- Liquidity availability
- Regulatory compliance
- Technical feasibility

Request new assets through support.
    `.trim(),
  },
  {
    id: "dep-003",
    slug: "network-chain-selection",
    title: "Network/Chain Selection",
    description: "Choosing the right network for your deposit",
    category: "deposits",
    order: 3,
    keywords: ["network", "chain", "blockchain", "selection", "choose"],
    content: `
# Network/Chain Selection

Selecting the correct network is crucial for successful deposits.

## Why Network Matters

- Each blockchain is independent
- Same asset exists on multiple networks
- Wrong network = lost funds
- Different fees and speeds

## Available Networks

### Ethereum
- Most widely supported
- Higher gas fees
- Maximum security
- ~3 minute finality

### Polygon
- Low fees
- Fast confirmations
- EVM compatible
- Great for stablecoins

### Base
- Very low fees
- Built by Coinbase
- Fast transactions
- Growing ecosystem

### Solana
- Ultra-fast
- Very low fees
- Non-EVM
- Different address format

## Choosing a Network

### For Low Fees
1. Solana
2. Base
3. Polygon
4. Arbitrum

### For Security
1. Ethereum
2. Base
3. Polygon
4. Arbitrum

### For Speed
1. Solana
2. Base
3. Polygon
4. Arbitrum

## Common Mistakes

**Sending ETH to Polygon address**
Each network address is unique. Always match the network.

**Using wrong token standard**
USDC on Ethereum ≠ USDC on Solana (different contracts)

**Insufficient gas**
Ensure you have native tokens for fees

## How to Check Network

In Supabank:
1. Go to your liquidation address
2. Network is clearly displayed
3. Only send matching network assets

In your wallet:
1. Check selected network
2. Verify token contract
3. Confirm before sending
    `.trim(),
  },
  {
    id: "dep-004",
    slug: "deposit-processing-times",
    title: "Deposit Processing Times",
    description: "How long deposits take to process",
    category: "deposits",
    order: 4,
    keywords: ["processing", "time", "duration", "waiting", "confirmation"],
    content: `
# Deposit Processing Times

Understand how long it takes for your deposit to be processed.

## Processing Stages

### 1. Blockchain Detection (Instant - 1 min)
- We detect your transaction
- Status: "Detecting"

### 2. Confirmations (Varies by network)
- Waiting for blockchain confirmations
- Status: "Confirming"

### 3. Conversion (1-2 minutes)
- Converting crypto to fiat
- Status: "Converting"

### 4. Settlement (Instant)
- Fiat added to balance
- Status: "Completed"

## Network Confirmation Times

| Network | Confirmations | Typical Time |
|---------|---------------|--------------|
| Ethereum | 12 | 2-5 min |
| Polygon | 128 | 3-8 min |
| Base | 12 | 30 sec - 1 min |
| Arbitrum | 12 | 1-3 min |
| Solana | 32 | 15-30 sec |

## Factors Affecting Speed

### Network Congestion
- High traffic = slower
- Check network status
- Peak times: 9am-5pm UTC

### Gas Price Used
- Higher gas = faster
- Low gas may delay inclusion
- Some wallets auto-optimize

### Amount
- Large amounts may need extra verification
- Standard verification is automatic

## Tracking Your Deposit

1. View in Transactions tab
2. Click for detailed status
3. See blockchain confirmation progress
4. Estimated completion time shown

## Delayed Deposits

If deposit takes longer than expected:
1. Check blockchain explorer
2. Verify transaction confirmed
3. Ensure correct network used
4. Contact support if >1 hour delay
    `.trim(),
  },
  {
    id: "dep-005",
    slug: "minimum-deposit-amounts",
    title: "Minimum Deposit Amounts",
    description: "Minimum amounts for crypto deposits",
    category: "deposits",
    order: 5,
    keywords: ["minimum", "amount", "deposit", "limit", "threshold"],
    content: `
# Minimum Deposit Amounts

All deposits must meet minimum thresholds to be processed.

## Understanding Minimums

### Variable Minimums

Minimum deposit amounts are **NOT fixed** - they vary based on:
- **Asset type:** Stablecoins vs ETH have different requirements
- **Network:** Ethereum mainnet has higher minimums than Layer 2 networks (Base, Polygon)
- **Market conditions:** Network congestion can affect minimums

**Always use the calculator** in your dashboard to determine the exact minimum for your specific asset and network combination before sending.

### Why Minimums Exist

- Network fees make small deposits economically inefficient
- Processing costs per transaction
- Regulatory compliance requirements
- Quality of service maintenance

## How Minimums Are Calculated

Minimums are determined by:
1. **Base asset minimum** (varies by asset type)
2. **Network-specific requirements** (Ethereum requires higher minimums due to gas costs)
3. **Currency conversion** (if depositing to non-USD fiat)

### Typical Minimums by Network

**Low-fee networks (Base, Polygon, Solana):**
- USDC/USDT: ~$10
- ETH: ~0.003 ETH

**High-fee networks (Ethereum mainnet):**
- USDC/USDT: ~$20+
- ETH: ~0.003 ETH (~$10-15)

**Note:** These are estimates. Use the calculator for exact amounts.

## What Happens Below Minimum?

### Deposits Below Minimum
- Transaction detected on blockchain
- Not processed automatically
- Contact support for assistance
- May be credited after fee deduction

### Dust Amounts (< $1-5)
- May not be recoverable
- Network fees often exceed the deposit value
- Avoid sending very small amounts

## Combining Deposits

You cannot combine multiple small deposits. Each transaction must meet the minimum independently.

## Tips

Calculate USD value before sending
Include some buffer above minimum
Check current exchange rates
Consider network fees in total
    `.trim(),
  },
  {
    id: "dep-006",
    slug: "failed-deposits-troubleshooting",
    title: "Failed Deposits Troubleshooting",
    description: "What to do when a deposit fails",
    category: "deposits",
    order: 6,
    keywords: ["failed", "error", "troubleshooting", "problem", "stuck"],
    content: `
# Failed Deposits Troubleshooting

Sometimes deposits don't go as expected. Here's how to resolve issues.

## Common Issues

### 1. Wrong Network
**Problem:** Sent to correct address but wrong network

**Solution:**
- Funds may be recoverable (varies by situation)
- Contact support with transaction details
- Recovery may take time and fees

### 2. Below Minimum
**Problem:** Deposit below network-specific minimum

**Solution:**
- Transaction detected but not processed automatically
- Contact support for manual review
- Fees may be deducted from recovery

### 3. Unsupported Token
**Problem:** Sent non-supported token

**Solution:**
- Recovery may not be possible
- Contact support immediately
- Include token contract address

### 4. Insufficient Confirmations
**Problem:** Transaction confirmed but deposit not showing

**Solution:**
- Wait for required confirmations
- Check network status
- Usually resolves within 30 minutes

## Troubleshooting Steps

### Step 1: Verify Transaction
1. Get your transaction hash
2. Check blockchain explorer
3. Confirm transaction succeeded
4. Note: confirmations, network, amount

### Step 2: Check in Supabank
1. Go to Transactions
2. Look for pending deposits
3. Check status and progress
4. Note any error messages

### Step 3: Contact Support
If deposit not appearing after 1 hour:
1. Email support@supabank.xyz
2. Include:
   - Transaction hash
   - Network used
   - Amount sent
   - Your Supabank email
   - Screenshot of transaction

## Recovery Times

| Issue | Typical Resolution |
|-------|-------------------|
| Delayed confirmation | 1-2 hours |
| Below minimum | 1-2 business days |
| Wrong network | 3-7 business days |
| Unsupported token | Case by case |

## Prevention

Double-check network before sending
Verify address carefully
Send test amount first for large deposits
Use supported assets only
    `.trim(),
  },
  {
    id: "dep-007",
    slug: "deposit-confirmations",
    title: "Deposit Confirmations",
    description: "Understanding blockchain confirmations",
    category: "deposits",
    order: 7,
    keywords: ["confirmations", "blockchain", "pending", "confirmed", "verify"],
    content: `
# Deposit Confirmations

Learn about blockchain confirmations and why they matter.

## What Are Confirmations?

When you send crypto:
1. Transaction broadcasts to network
2. Included in a block (1 confirmation)
3. More blocks added on top
4. Each new block = 1 more confirmation

## Why Confirmations Matter

- Prevents double-spending
- Ensures transaction finality
- Protects against reorganizations
- Required for security

## Required Confirmations

| Network | Confirmations | Why |
|---------|---------------|-----|
| Ethereum | 12 | High security standard |
| Polygon | 128 | PoS security model |
| Base | 12 | L2 with ETH security |
| Arbitrum | 12 | L2 with ETH security |
| Solana | 32 | Fast finality |
| Tron | 20 | Network standard |

## Confirmation Timeline

### Example: Ethereum
- 0 conf: Transaction submitted
- 1 conf: In first block (~12 sec)
- 12 conf: Fully confirmed (~2-3 min)

### Example: Solana
- 0 conf: Transaction submitted
- 1 conf: First slot (~400ms)
- 32 conf: Fully confirmed (~15 sec)

## Tracking Confirmations

### In Supabank
1. Go to Transactions
2. Click pending deposit
3. See confirmation progress bar
4. Shows current / required

### On Blockchain
1. Copy transaction hash
2. Visit block explorer
3. Check "Confirmations" field

## Stuck Transactions

If confirmations aren't progressing:
- Network may be congested
- Low gas may delay inclusion
- Check network status page
- Wait or contact support
    `.trim(),
  },
];
